/**
 * v101.175.0 · Mensajes «premium» (estética Apple: vidrio CLARO por defecto, tipografía limpia, sombras suaves; estructura tipo Vercel:
 * título + origen + detalle + cómo resolverlo + acciones) y tarjeta de PROCESO para integraciones (estilo IA:
 * «Analizando…», paso actual, barra de progreso con %).
 *
 * Se instala UNA vez (import en index.js) y envuelve toast.success / error / warning / info de sonner: las ~850
 * llamadas existentes se ven con el diseño nuevo sin tocarlas. Si el mensaje no es texto (JSX), se usa el original.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Copy, Info, Sparkles, Wrench, X, XCircle, Cpu,
} from "lucide-react";

const ORIG = {
  success: toast.success, error: toast.error, warning: toast.warning, info: toast.info, message: toast.message,
};

const KIND = {
  error: {
    Icon: XCircle, label: "Error", fallback: "No se pudo completar la acción",
    ring: "rgba(248,113,113,0.35)", glow: "linear-gradient(90deg,#f43f5e,#fb923c)", tint: "rgba(244,63,94,0.14)",
    icon: "#fb7185", iconL: "#e11d48", duration: 16000,
  },
  warning: {
    Icon: AlertTriangle, label: "Atención", fallback: "Revise antes de continuar",
    ring: "rgba(251,191,36,0.35)", glow: "linear-gradient(90deg,#f59e0b,#fde047)", tint: "rgba(245,158,11,0.14)",
    icon: "#fbbf24", iconL: "#d97706", duration: 10000,
  },
  success: {
    Icon: CheckCircle2, label: "Listo", fallback: "Acción completada",
    ring: "rgba(52,211,153,0.35)", glow: "linear-gradient(90deg,#10b981,#22d3ee)", tint: "rgba(16,185,129,0.14)",
    icon: "#34d399", iconL: "#059669", duration: 5000,
  },
  info: {
    Icon: Info, label: "Información", fallback: "Información",
    ring: "rgba(96,165,250,0.35)", glow: "linear-gradient(90deg,#38bdf8,#a78bfa)", tint: "rgba(56,189,248,0.14)",
    icon: "#7dd3fc", iconL: "#0284c7", duration: 6000,
  },
};

/* Paletas: «light» (vidrio claro estilo Apple · POR DEFECTO, aprobado por el operador) y «dark».
   Para cambiarlo en un navegador: localStorage afc_toast_theme = light | dark | auto (auto = tema de la web). */
const PALETTES = {
  dark: {
    glassBg: "linear-gradient(180deg, rgba(28,31,40,0.86) 0%, rgba(14,16,22,0.9) 100%)",
    border: "rgba(255,255,255,0.09)", shadow: "0 24px 60px -16px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
    text: "#f5f5f7", textSoft: "rgba(235,235,245,0.72)", textItem: "rgba(235,235,245,0.78)", muted: "rgba(255,255,255,0.35)",
    chipText: "rgba(255,255,255,0.7)", chipBg: "rgba(255,255,255,0.07)", chipBorder: "rgba(255,255,255,0.10)",
    codeBg: "rgba(255,255,255,0.08)", codeBorder: "rgba(255,255,255,0.08)", codeText: "#e5e7eb", quote: "#f5f5f7",
    itemBg: "rgba(255,255,255,0.04)", itemBorder: "rgba(255,255,255,0.06)",
    btnText: "rgba(255,255,255,0.82)", btnBg: "rgba(255,255,255,0.06)", btnBorder: "rgba(255,255,255,0.10)", close: "rgba(255,255,255,0.5)",
    fixBg: "linear-gradient(135deg, rgba(167,139,250,0.12), rgba(56,189,248,0.10))", fixBorder: "rgba(167,139,250,0.22)",
    fixText: "rgba(237,233,254,0.92)", fixKicker: "rgba(196,181,253,0.85)", fixIcon: "#c4b5fd",
    statusText: "rgba(254,205,211,0.9)", statusBg: "rgba(244,63,94,0.10)", statusBorder: "rgba(244,63,94,0.25)",
    trackBg: "rgba(255,255,255,0.07)", trackBorder: "rgba(255,255,255,0.06)", orbBg: "rgba(10,12,18,0.85)", orbBorder: "rgba(255,255,255,0.12)",
    pct: "rgba(255,255,255,0.85)", tintAlpha: 1,
  },
  light: {
    glassBg: "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,252,0.88) 100%)",
    border: "rgba(15,23,42,0.08)", shadow: "0 24px 60px -18px rgba(15,23,42,0.28), 0 2px 8px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
    text: "#0f172a", textSoft: "#475569", textItem: "#334155", muted: "#94a3b8",
    chipText: "#475569", chipBg: "rgba(15,23,42,0.05)", chipBorder: "rgba(15,23,42,0.08)",
    codeBg: "rgba(15,23,42,0.05)", codeBorder: "rgba(15,23,42,0.08)", codeText: "#0f172a", quote: "#0f172a",
    itemBg: "rgba(15,23,42,0.025)", itemBorder: "rgba(15,23,42,0.06)",
    btnText: "#334155", btnBg: "#ffffff", btnBorder: "rgba(15,23,42,0.10)", close: "#94a3b8",
    fixBg: "linear-gradient(135deg, rgba(139,92,246,0.08), rgba(14,165,233,0.07))", fixBorder: "rgba(139,92,246,0.20)",
    fixText: "#3b2f6b", fixKicker: "#7c3aed", fixIcon: "#7c3aed",
    statusText: "#be123c", statusBg: "rgba(244,63,94,0.07)", statusBorder: "rgba(244,63,94,0.22)",
    trackBg: "rgba(15,23,42,0.06)", trackBorder: "rgba(15,23,42,0.05)", orbBg: "#ffffff", orbBorder: "rgba(15,23,42,0.08)",
    pct: "#0f172a", tintAlpha: 1,
  },
};
export function toastTheme() {
  let mode = "light";
  try { mode = localStorage.getItem("afc_toast_theme") || "auto"; } catch (_) { /* */ }
  if (mode === "light" || mode === "dark") return mode;
  try { return document.documentElement.classList.contains("dark") ? "dark" : "light"; } catch (_) { return "light"; }
}
const pal = () => PALETTES[toastTheme()] || PALETTES.light;
const glass = (P) => ({
  background: P.glassBg, backdropFilter: "blur(22px) saturate(160%)", WebkitBackdropFilter: "blur(22px) saturate(160%)",
  border: `1px solid ${P.border}`, boxShadow: P.shadow,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Inter', 'Segoe UI', Roboto, sans-serif",
});
const MONO = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, Consolas, monospace";

/* ───────────────────────── Análisis del texto ───────────────────────── */
const STATUS_RE = /\s*(La OT NO pas[oó] a [^.]+\.?|La OT no se modific[oó][^.]*\.?|No se modific[oó] nada[^.]*\.?)\s*$/i;
const FIX_RE = /(Qu[eé] hacer:|C[oó]mo resolverlo:|Corrija |Corregir |Revise |Verifique |Vuelva a |Pida a |Configure |Aprovisione |Quite |→)/;

export function parseMessage(raw, kind) {
  let s = String(raw ?? "").replace(/\.\.+/g, ".").replace(/\s+\n/g, "\n").trim();
  let title = "", source = "", status = "";
  const st = s.match(STATUS_RE);
  if (st) { status = st[1].trim(); s = s.slice(0, st.index).trim(); }
  // «Cierre bloqueado · Odoo: …» / «Odoo · cierre falló: …»
  let m = s.match(/^([^:\n]{3,70}?)\s*·\s*([^:\n·]{2,32}):\s*([\s\S]+)$/);
  if (m) { title = m[1].trim(); source = m[2].trim(); s = m[3].trim(); }
  else if ((m = s.match(/^([^:\n]{3,48}):\s+([\s\S]{12,})$/)) && !/\d{3,}/.test(m[1])) { title = m[1].trim(); s = m[2].trim(); }
  // Origen al inicio del cuerpo: «Odoo: …», «Cierre detenido: …»
  if (!source) {
    const so = s.match(/^(Odoo|OLT|Axxess|WhatsApp|Inventario)\b[:\s·-]+/i);
    if (so) { source = so[1]; s = s.slice(so[0].length).trim(); }
  }
  // Lista: «• a • b» o líneas con «- »
  let items = [];
  const parts = s.split(/\s*(?:\n\s*[•\-–]\s+|\s•\s+|^•\s+)/);
  if (parts.length > 1) { s = parts[0].trim(); items = parts.slice(1).map((x) => x.trim()).filter(Boolean); }
  // Cómo resolverlo: desde «Qué hacer:» (o la última frase con verbo de acción si es larga)
  let fix = "";
  const qh = s.match(/(Qu[eé] hacer:|C[oó]mo resolverlo:)\s*([\s\S]+)$/i);
  if (qh) { fix = qh[2].trim(); s = s.slice(0, qh.index).trim(); }
  else if (items.length) {
    const last = items[items.length - 1];
    const qi = last.match(/(Qu[eé] hacer:)\s*([\s\S]+)$/i);
    if (qi) { fix = qi[2].trim(); items[items.length - 1] = last.slice(0, qi.index).trim(); }
  }
  if (!fix && kind !== "success") {
    // Sin lookbehind (Safari < 16.4 no lo soporta y rompería toda la web)
    const sentences = [];
    s.split(/([.!?])\s+(?=[A-ZÁÉÍÓÚÑ¿(«])/).forEach((chunk, i, arr) => {
      if (i % 2 === 0) sentences.push(chunk + (arr[i + 1] || ""));
    });
    if (sentences.length > 1 && FIX_RE.test(sentences[sentences.length - 1])) {
      fix = sentences.pop().trim();
      s = sentences.join(" ").trim();
    }
  }
  if (s) s = s.charAt(0).toUpperCase() + s.slice(1);
  if (fix) fix = fix.charAt(0).toUpperCase() + fix.slice(1);
  if (!title) {
    if (s.length <= 72 && !items.length && !fix) { title = s; s = ""; }
    else title = KIND[kind]?.fallback || "Mensaje";
  }
  return { title, source, body: s, items: items.filter(Boolean), fix, status };
}

/** Resalta series / códigos / IPs en monoespaciado */
function Rich({ text }) {
  const P = pal();
  const parts = useMemo(() => {
    const re = /(\b\d{1,3}(?:\.\d{1,3}){3}\b|«[^»]{1,60}»|\(G\)\s?[A-Z0-9]{6,}|\b(?=[A-Z0-9-]*\d)(?=[A-Z0-9-]*[A-Z])[A-Z0-9][A-Z0-9-]{6,}\b|\b0{2}\d{6}\b)/g;
    const out = []; let last = 0; let m;
    const t = String(text || "");
    while ((m = re.exec(t))) {
      if (m.index > last) out.push(t.slice(last, m.index));
      out.push({ code: m[0] });
      last = m.index + m[0].length;
    }
    if (last < t.length) out.push(t.slice(last));
    return out;
  }, [text]);
  return (
    <>
      {parts.map((p, i) => (typeof p === "string" ? <React.Fragment key={i}>{p}</React.Fragment> : p.code.startsWith("«") ? (
        <span key={i} style={{ color: P.quote, fontWeight: 500 }}>{p.code}</span>
      ) : (
        <code key={i} style={{
          fontFamily: MONO, fontSize: "0.92em", padding: "1px 6px", borderRadius: 6,
          background: P.codeBg, border: `1px solid ${P.codeBorder}`, color: P.codeText,
          whiteSpace: "nowrap",
        }}>{p.code}</code>
      )))}
    </>
  );
}

const btnS = (P) => ({
  display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 10px", borderRadius: 8,
  fontSize: 12, fontWeight: 500, color: P.btnText, background: P.btnBg,
  border: `1px solid ${P.btnBorder}`, cursor: "pointer",
});

/* ───────────────────────── Tarjeta de mensaje ───────────────────────── */
function PremiumToast({ id, kind, raw, description }) {
  const k = KIND[kind] || KIND.info;
  const p = useMemo(() => {
    const base = parseMessage(raw, kind);
    if (description && typeof description === "string") base.body = [base.body, description].filter(Boolean).join(" ");
    return base;
  }, [raw, kind, description]);
  const long = (p.body.length > 220) || p.items.length > 3;
  const [open, setOpen] = useState(!long);
  const [copied, setCopied] = useState(false);
  const time = useMemo(() => new Date().toLocaleTimeString("es-EC", { hour12: false }), []);
  const copy = () => {
    const txt = [p.title + (p.source ? ` · ${p.source}` : ""), p.body, ...p.items.map((x) => `• ${x}`),
      p.fix && `Cómo resolverlo: ${p.fix}`, p.status].filter(Boolean).join("\n");
    try { navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch (_) { /* */ }
  };
  const Icon = k.Icon;
  const P = pal();
  const light = P === PALETTES.light;
  const ic = light ? k.iconL : k.icon;
  const btn = btnS(P);
  const hasDetail = kind !== "success" && (p.body || p.items.length || p.fix);
  return (
    <div role={kind === "error" ? "alert" : "status"} data-testid={`ptoast-${kind}`}
      style={{ ...glass(P), width: 392, maxWidth: "calc(100vw - 32px)", borderRadius: 18, overflow: "hidden", color: P.text,
               animation: "ptoast-in 380ms cubic-bezier(.2,.9,.3,1.2)" }}>
      <div style={{ height: 2, background: k.glow, boxShadow: `0 0 18px 1px ${k.ring}` }} />
      <div style={{ padding: "14px 14px 12px 14px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: k.tint, display: "grid", placeItems: "center",
                        boxShadow: `0 0 0 1px ${k.ring} inset`, flexShrink: 0 }}>
            <Icon size={18} color={ic} strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.3 }}>
                <Rich text={p.title} />
              </span>
              {p.source && (
                <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
                               padding: "2px 7px", borderRadius: 999, color: P.chipText,
                               background: P.chipBg, border: `1px solid ${P.chipBorder}` }}>
                  {p.source}
                </span>
              )}
            </div>
            {p.body && (open || !long) && (
              <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.55, color: P.textSoft, whiteSpace: "pre-line" }}>
                <Rich text={p.body} />
              </div>
            )}
            {p.body && long && !open && (
              <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.55, color: P.textSoft,
                            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                <Rich text={p.body} />
              </div>
            )}
            {p.items.length > 0 && open && (
              <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
                {p.items.map((it, i) => (
                  <li key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: P.textItem,
                                       padding: "8px 10px", borderRadius: 10, background: P.itemBg,
                                       border: `1px solid ${P.itemBorder}` }}>
                    <span style={{ width: 6, height: 6, borderRadius: 999, background: ic, marginTop: 7, flexShrink: 0,
                                   boxShadow: `0 0 8px ${k.ring}` }} />
                    <span style={{ whiteSpace: "pre-line" }}><Rich text={it} /></span>
                  </li>
                ))}
              </ul>
            )}
            {p.fix && open && (
              <div style={{ marginTop: 10, padding: "9px 11px", borderRadius: 12, display: "flex", gap: 9,
                            background: P.fixBg,
                            border: `1px solid ${P.fixBorder}` }}>
                <Wrench size={14} color={P.fixIcon} style={{ marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: P.fixText }}>
                  <div style={{ fontSize: 10, fontFamily: MONO, letterSpacing: "0.08em", textTransform: "uppercase",
                                color: P.fixKicker, marginBottom: 2 }}>Cómo resolverlo</div>
                  <Rich text={p.fix} />
                </div>
              </div>
            )}
            {p.status && (
              <div style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11,
                            padding: "3px 9px", borderRadius: 999, color: P.statusText,
                            background: P.statusBg, border: `1px solid ${P.statusBorder}` }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#fb7185" }} /> {p.status}
              </div>
            )}
          </div>
          <button aria-label="Cerrar" onClick={() => toast.dismiss(id)}
            style={{ ...btn, width: 26, height: 26, padding: 0, justifyContent: "center", background: "transparent",
                     border: "1px solid transparent", color: P.close }}>
            <X size={15} />
          </button>
        </div>
        {hasDetail && (
          <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
            {long && (
              <button style={btn} onClick={() => setOpen((v) => !v)}>
                {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Ver menos" : "Ver detalle"}
              </button>
            )}
            <button style={btn} onClick={copy}><Copy size={13} /> {copied ? "Copiado" : "Copiar detalle"}</button>
            <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 10.5, color: P.muted }}>
              {k.label} · {time}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── Tarjeta de PROCESO (integraciones) ───────────────────────── */
export const PROCESS_KINDS = {
  close: {
    title: "Procesando el cierre con Odoo", tau: 14,
    steps: ["Validando materiales y series", "Revisando la bodega del técnico", "Verificando devoluciones de equipos",
            "Armando el cierre de la tarea", "Enviando a Odoo", "Esperando confirmación de Odoo"],
  },
  provision: {
    title: "Aprovisionando el servicio", tau: 16,
    steps: ["Conectando con la OLT", "Validando caja, puerto y tarjeta", "Registrando la ONT", "Configurando VLAN e IP",
            "Verificando el servicio", "Notificando a Odoo"],
  },
  desaprov: {
    title: "Desaprovisionando el servicio", tau: 12,
    steps: ["Conectando con la OLT", "Liberando el puerto", "Retirando la configuración", "Notificando a Odoo"],
  },
  sync: {
    title: "Sincronizando con Odoo", tau: 8,
    steps: ["Consultando Odoo", "Comparando con AlfaField", "Actualizando los datos", "Recalculando estados"],
  },
  client: {
    title: "Actualizando el cliente en Odoo", tau: 5,
    steps: ["Conectando con Odoo", "Escribiendo los datos del cliente", "Confirmando el cambio"],
  },
  cancel: {
    title: "Cancelando la tarea", tau: 7,
    steps: ["Validando la OT", "Cancelando en Odoo", "Liberando recursos reservados"],
  },
  generic: {
    title: "Procesando con la integración", tau: 8,
    steps: ["Conectando con el servicio", "Procesando la solicitud", "Esperando respuesta"],
  },
};

function ProcessToast({ id, kind, startedAt, finished, ok }) {
  const cfg = PROCESS_KINDS[kind] || PROCESS_KINDS.generic;
  const P = pal();
  const btn = btnS(P);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (finished) return undefined;
    const t = setInterval(() => setNow(Date.now()), 180);
    return () => clearInterval(t);
  }, [finished]);
  const sec = Math.max(0, (now - startedAt) / 1000);
  const pct = finished ? 100 : Math.min(94, Math.round(94 * (1 - Math.exp(-sec / cfg.tau))));
  const stepIdx = finished ? cfg.steps.length - 1 : Math.min(cfg.steps.length - 1, Math.floor((pct / 94) * cfg.steps.length));
  const step = finished ? (ok ? "Completado" : "Finalizó con observaciones") : cfg.steps[stepIdx];
  const accent = finished ? (ok ? "linear-gradient(90deg,#10b981,#22d3ee)" : "linear-gradient(90deg,#f43f5e,#fb923c)")
    : "linear-gradient(90deg,#22d3ee,#818cf8,#c084fc)";
  return (
    <div role="status" aria-live="polite" data-testid={`pprocess-${kind}`}
      style={{ ...glass(P), width: 392, maxWidth: "calc(100vw - 32px)", borderRadius: 18, overflow: "hidden", color: P.text,
               animation: "ptoast-in 380ms cubic-bezier(.2,.9,.3,1.2)" }}>
      <div style={{ padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ position: "relative", width: 34, height: 34, flexShrink: 0 }}>
            <div style={{ position: "absolute", inset: 0, borderRadius: 11, background: accent, opacity: 0.9,
                          filter: "blur(10px)", animation: finished ? "none" : "ptoast-pulse 1.8s ease-in-out infinite" }} />
            <div style={{ position: "absolute", inset: 0, borderRadius: 11, display: "grid", placeItems: "center",
                          background: P.orbBg, border: `1px solid ${P.orbBorder}` }}>
              {finished ? (ok ? <CheckCircle2 size={18} color="#34d399" /> : <XCircle size={18} color="#fb7185" />)
                : <Cpu size={18} color={P === PALETTES.light ? "#6366f1" : "#a5b4fc"} style={{ animation: "ptoast-spin 6s linear infinite" }} />}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Sparkles size={13} color={P.fixIcon} />
              <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>{cfg.title}</span>
            </div>
            <div key={step} style={{ marginTop: 3, fontSize: 12.5, color: P.textSoft,
                                     animation: "ptoast-fade 420ms ease" }}>
              {step}{finished ? "" : <span className="ptoast-dots" />}
            </div>
          </div>
          {finished && (
            <button aria-label="Cerrar" onClick={() => toast.dismiss(id)}
              style={{ ...btn, width: 26, height: 26, padding: 0, justifyContent: "center", background: "transparent",
                       border: "1px solid transparent", color: P.close }}><X size={15} /></button>
          )}
        </div>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative", flex: 1, height: 8, borderRadius: 999, overflow: "hidden",
                        background: P.trackBg, border: `1px solid ${P.trackBorder}` }}>
            <div style={{ position: "absolute", inset: 0, width: `${pct}%`, background: accent, borderRadius: 999,
                          transition: "width 400ms cubic-bezier(.2,.8,.2,1)", boxShadow: "0 0 14px rgba(129,140,248,0.55)" }} />
            {!finished && <div className="ptoast-shimmer" style={{ width: `${pct}%` }} />}
          </div>
          <span style={{ fontFamily: MONO, fontSize: 12, fontVariantNumeric: "tabular-nums", minWidth: 38, textAlign: "right",
                         color: P.pct }}>{pct}%</span>
        </div>
        <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", fontFamily: MONO, fontSize: 10.5,
                      color: P.muted }}>
          <span>AlfaField · integración</span>
          <span>{Math.round(sec)} s</span>
        </div>
      </div>
    </div>
  );
}

/** Inicia la tarjeta de proceso. Devuelve { finish(ok) } */
export function startProcess(kind = "generic") {
  const id = `proc-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const startedAt = Date.now();
  toast.custom((t) => <ProcessToast id={t} kind={kind} startedAt={startedAt} />, { id, duration: Infinity });
  return {
    id,
    finish(ok = true) {
      toast.custom((t) => <ProcessToast id={t} kind={kind} startedAt={startedAt} finished ok={ok} />,
        { id, duration: ok ? 900 : 600 });
    },
  };
}

/* ───────────────────────── Instalación ───────────────────────── */
let installed = false;
export function installPremiumToasts() {
  if (installed) return;
  installed = true;
  if (typeof document !== "undefined" && !document.getElementById("ptoast-css")) {
    const css = document.createElement("style");
    css.id = "ptoast-css";
    css.textContent = `
@keyframes ptoast-in{from{opacity:0;transform:translateY(-8px) scale(.98);filter:blur(4px)}to{opacity:1;transform:none;filter:none}}
@keyframes ptoast-fade{from{opacity:0;transform:translateY(3px)}to{opacity:1;transform:none}}
@keyframes ptoast-pulse{0%,100%{opacity:.55;transform:scale(.92)}50%{opacity:1;transform:scale(1.08)}}
@keyframes ptoast-spin{to{transform:rotate(360deg)}}
@keyframes ptoast-sh{from{transform:translateX(-100%)}to{transform:translateX(220%)}}
.ptoast-shimmer{position:absolute;top:0;bottom:0;left:0;overflow:hidden;border-radius:999px;pointer-events:none}
.ptoast-shimmer::after{content:"";position:absolute;top:0;bottom:0;width:40%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent);animation:ptoast-sh 1.4s ease-in-out infinite}
.ptoast-dots::after{content:"";display:inline-block;width:1.2em;text-align:left;animation:ptoast-dots 1.2s steps(4,end) infinite}
@keyframes ptoast-dots{0%{content:""}25%{content:"."}50%{content:".."}75%{content:"..."}}
@media (prefers-reduced-motion: reduce){[data-testid^="ptoast"],[data-testid^="pprocess"],.ptoast-shimmer::after{animation:none!important}}
`;
    document.head.appendChild(css);
  }
  const wrap = (kind) => (message, opts = {}) => {
    if (message === null || message === undefined || (typeof message !== "string" && typeof message !== "number")) {
      return ORIG[kind](message, opts);
    }
    const k = KIND[kind] || KIND.info;
    const { description, duration, id, ...rest } = opts || {};
    return toast.custom((t) => <PremiumToast id={t} kind={kind} raw={String(message)} description={description} />,
      { ...rest, id, duration: duration ?? k.duration });
  };
  toast.success = wrap("success");
  toast.error = wrap("error");
  toast.warning = wrap("warning");
  toast.info = wrap("info");
  toast.process = startProcess;
}

installPremiumToasts();
